import {
    Box,
    Heading,
    VStack,
    FormControl,
    FormLabel,
    FormHelperText,
    Input,
    Button,
    IconButton,
    useToast,
    HStack,
    FormErrorMessage,
    Image,
    CloseButton,
    Text,
    Flex,
    SimpleGrid,
    Badge,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    faEdit,
    faSave,
    faUser,
    faLock,
    faImage,
    faIdCard,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth } from "../../contexts/AuthContext";
import { useMutation } from "@tanstack/react-query";
import { ROLES } from "../../constants";
import { signOut, updateUser } from "../../services/authService";
import { preparePhotosForUpload, truncateName } from "../../utils/helpers";
import { optionalTelephoneYup } from "../../utils/phoneValidation";
import { useDropZoneUpload } from "../../utils/customHooks";
import { usePhotoUpload } from "../../utils/sharedMutations";
import { submitLogoToForeignPartner } from "../../services/users";
import { isOrganizationAccount } from "../../utils/userProfile";
import PasswordInput from "../../components/password-input/PasswordInput";

const inputStyles = {
    bg: "gray.50",
    borderColor: "gray.200",
    borderRadius: "md",
    _hover: { borderColor: "teal.300" },
    _focusVisible: {
        borderColor: "teal.400",
        boxShadow: "0 0 0 1px var(--chakra-colors-teal-400)",
    },
    _disabled: {
        opacity: 0.85,
        cursor: "not-allowed",
        bg: "gray.100",
    },
};

const SectionCard = ({ icon, title, children }) => (
    <Box
        bg="white"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="xl"
        p={{ base: 5, md: 6 }}
        boxShadow="sm"
    >
        <HStack spacing={2} mb={5}>
            <Flex
                align="center"
                justify="center"
                w="32px"
                h="32px"
                borderRadius="md"
                bg="teal.50"
                color="teal.600"
            >
                <FontAwesomeIcon icon={icon} size="sm" />
            </Flex>
            <Heading size="sm" color="gray.800" fontWeight="semibold">
                {title}
            </Heading>
        </HStack>
        <VStack spacing={4} align="stretch">
            {children}
        </VStack>
    </Box>
);

const UserDashboard = () => {
    const { t } = useTranslation("common");
    const [editMode, setEditMode] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    const { currentUser, isLoadingUser, clearAuthSession } = useAuth();

    const updateUserMutation = useMutation({
        mutationFn: (userData) => updateUser(userData),
        onError: (error) => {
            const apiError = error.response?.data?.error;
            const message = Array.isArray(apiError)
                ? apiError[0]
                : apiError || t("profileUpdateErrorMessage");
            toast({
                title: message,
                status: "error",
            });
        },
        onSuccess: (response) => {
            const data = response?.data;
            if (data?.reconfirmationSent) {
                toast({
                    title: t("emailReconfirmationSentMessage", {
                        email: data.unconfirmedEmail,
                    }),
                    status: "success",
                    duration: 8000,
                });
            } else {
                toast({
                    title: t("profileUpdateSuccessMessage"),
                    status: "success",
                });
            }
        },
    });

    const {
        selectedFiles,
        cleanBuffer,
        getRootProps,
        getInputProps,
        handleRemoveFile,
    } = useDropZoneUpload(currentUser.images, () => true);

    const submitPhotosMutation = usePhotoUpload(({ userId, photos }) =>
        submitLogoToForeignPartner(userId, photos)
    );

    const isPartner =
        currentUser?.type === ROLES.FOREIGN_PARTNER ||
        currentUser?.type === ROLES.DOMESTIC_PARTNER;
    const isOrgPartner = isPartner && isOrganizationAccount(currentUser);
    const isGuest = currentUser?.type === ROLES.GUEST;

    const accountKindLabel =
        currentUser?.accountKind === "organization"
            ? t("accountKindOrganization")
            : t("accountKindIndividual");

    const initialFormValues =
        isLoadingUser || !currentUser
            ? {
                firstName: "",
                lastName: "",
                organizationName: "",
                contactPersonName: "",
                email: "",
                telephoneNumber: "",
                nameOnPassport: "",
                passportExpiresAt: "",
            }
            : {
                firstName: currentUser.firstName || "",
                lastName: currentUser.lastName || "",
                organizationName: currentUser.organizationName || "",
                contactPersonName: currentUser.contactPersonName || "",
                email: currentUser.email,
                telephoneNumber:
                    currentUser.telephoneNumber != null
                        ? `${currentUser.telephoneNumber}`
                        : "",
                nameOnPassport: currentUser.nameOnPassport || "",
                passportExpiresAt: currentUser.passportExpiresAt
                    ? currentUser.passportExpiresAt.slice(0, 10)
                    : "",
            };

    const onSubmit = async (values, images) => {
        const {
            currentPassword,
            newPassword,
            confirmPassword,
            ...profileValues
        } = values;

        const emailChanged =
            profileValues.email?.toLowerCase().trim() !==
            currentUser.email?.toLowerCase().trim();

        const payload = { ...profileValues };
        if (newPassword || emailChanged) {
            payload.currentPassword = currentPassword;
        }
        if (newPassword) {
            payload.newPassword = newPassword;
            payload.confirmPassword = confirmPassword;
        }

        try {
            await updateUserMutation.mutateAsync(payload);
            if (images.length > 0) {
                await submitPhotosMutation.mutateAsync({
                    userId: currentUser.id,
                    photos: preparePhotosForUpload(images),
                });
            }

            if (newPassword) {
                try {
                    await signOut();
                } catch (_error) {
                    // Continue to login even if the sign-out request fails.
                }

                clearAuthSession();
                navigate("/login", { replace: true });
                return;
            }

            window.location.reload();
        } catch (error) {}
    };

    const logoSection = currentUser?.type === ROLES.FOREIGN_PARTNER && (
        editMode ? (
            <SectionCard icon={faImage} title={t("logo")}>
                <Box
                    {...getRootProps()}
                    p={6}
                    border="2px dashed"
                    borderColor="teal.200"
                    borderRadius="lg"
                    bg="teal.50"
                    cursor="pointer"
                    transition="all 0.2s"
                    textAlign="center"
                    _hover={{ borderColor: "teal.400", bg: "teal.100" }}
                >
                    <input {...getInputProps()} />
                    <Text fontSize="sm" color="teal.700">{t("dropZoneMessage")}</Text>
                </Box>
                {selectedFiles.some((img) => !img._destroy) && (
                    <HStack wrap="wrap" spacing={4} mt={2}>
                        {selectedFiles.map((image, index) => {
                            if (image._destroy) return null;
                            return (
                                <Box
                                    key={index}
                                    p={2}
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    borderRadius="lg"
                                    bg="gray.50"
                                    position="relative"
                                >
                                    <Image
                                        src={image.publicUrl}
                                        maxW="80px"
                                        maxH="80px"
                                        objectFit="contain"
                                        borderRadius="md"
                                    />
                                    <CloseButton
                                        size="sm"
                                        position="absolute"
                                        top="-8px"
                                        right="-8px"
                                        bg="white"
                                        borderWidth="1px"
                                        borderColor="gray.200"
                                        borderRadius="full"
                                        onClick={() => handleRemoveFile(index, image.id)}
                                    />
                                </Box>
                            );
                        })}
                    </HStack>
                )}
            </SectionCard>
        ) : (
            <SectionCard icon={faImage} title={t("logo")}>
                <Text mb={3} fontSize="sm" color="gray.500">
                    {selectedFiles.length > 0
                        ? t("logoImageLabel")
                        : t("logoEmptyEditHint")}
                </Text>
                {selectedFiles.some((img) => !img._destroy) ? (
                    <HStack wrap="wrap" spacing={4}>
                        {selectedFiles.map((image, index) => {
                            if (image._destroy) return null;
                            return (
                                <Box
                                    key={index}
                                    p={3}
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    borderRadius="xl"
                                    bg="gray.50"
                                    textAlign="center"
                                >
                                    <Image
                                        maxW="100px"
                                        maxH="100px"
                                        objectFit="contain"
                                        src={image.publicUrl}
                                        borderRadius="md"
                                        mx="auto"
                                    />
                                    <Text isTruncated fontSize="xs" color="gray.500" mt={2} maxW="100px">
                                        {truncateName(image.publicUrl)}
                                    </Text>
                                </Box>
                            );
                        })}
                    </HStack>
                ) : (
                    <Box
                        py={8}
                        textAlign="center"
                        bg="gray.50"
                        borderRadius="lg"
                        borderWidth="1px"
                        borderColor="gray.200"
                    >
                        <Flex
                            w="48px"
                            h="48px"
                            mx="auto"
                            mb={2}
                            align="center"
                            justify="center"
                            borderRadius="full"
                            bg="teal.50"
                            color="teal.400"
                        >
                            <FontAwesomeIcon icon={faImage} />
                        </Flex>
                        <Text fontSize="sm" color="gray.400">{t("noLogoUploaded")}</Text>
                    </Box>
                )}
            </SectionCard>
        )
    );

    return (
        <Box bg="gray.50" minH="calc(100vh - 80px)" py={{ base: 6, md: 10 }}>
            <Box maxW="720px" mx="auto" px={{ base: 4, md: 6 }}>
                <Flex
                    justify="space-between"
                    align={{ base: "flex-start", md: "center" }}
                    direction={{ base: "column", md: "row" }}
                    gap={4}
                    mb={8}
                >
                    <Box>
                        <Heading size="lg" color="teal.600" mb={2}>
                            {t("userDashboard")}
                        </Heading>
                        <Text color="gray.600" fontSize="sm">
                            {t("userDashboardSubtitle")}
                        </Text>
                    </Box>
                    <HStack spacing={3}>
                        {editMode && (
                            <Badge colorScheme="teal" variant="subtle" borderRadius="full" px={3}>
                                {t("editingBadge")}
                            </Badge>
                        )}
                        <IconButton
                            icon={<FontAwesomeIcon icon={editMode ? faSave : faEdit} />}
                            onClick={() => {
                                setEditMode((prev) => {
                                    if (prev) {
                                        setShowCurrentPassword(false);
                                        setShowNewPassword(false);
                                        setShowConfirmPassword(false);
                                    }
                                    return !prev;
                                });
                            }}
                            colorScheme="teal"
                            variant={editMode ? "solid" : "outline"}
                            borderRadius="full"
                            size="lg"
                            aria-label={t("editProfileAriaLabel")}
                        />
                    </HStack>
                </Flex>

                <Formik
                    enableReinitialize
                    initialValues={{
                        ...initialFormValues,
                        currentPassword: "",
                        newPassword: "",
                        confirmPassword: "",
                    }}
                    validationSchema={Yup.object().shape({
                        email: Yup.string()
                            .email(t("invalidEmailMessage"))
                            .required(t("requiredFieldMessage")),
                        telephoneNumber: optionalTelephoneYup(t),
                        currentPassword: Yup.string().when(["newPassword", "email"], {
                            is: (newPassword, email) => {
                                const emailChanged =
                                    email?.toLowerCase().trim() !==
                                    currentUser?.email?.toLowerCase().trim();
                                return !!newPassword || emailChanged;
                            },
                            then: (schema) => schema.required(t("requiredFieldMessage")),
                            otherwise: (schema) => schema.nullable(),
                        }),
                        newPassword: Yup.string()
                            .min(6, t("passwordTooShortMessage"))
                            .nullable(),
                        confirmPassword: Yup.string().when("newPassword", {
                            is: (value) => !!value,
                            then: (schema) =>
                                schema
                                    .required(t("requiredFieldMessage"))
                                    .oneOf([Yup.ref("newPassword"), null], t("passwordsDoNotMatchMessage")),
                            otherwise: (schema) => schema.nullable(),
                        }),
                    })}
                    onSubmit={(values, actions) => {
                        const newlyAddedFiles = selectedFiles
                            .filter((file) => !file.id)
                            .map((file) => file.file);

                        onSubmit(
                            {
                                ...values,
                                images_attributes: selectedFiles.filter((file) => file.id),
                            },
                            newlyAddedFiles
                        );

                        actions.resetForm();
                        cleanBuffer();
                    }}
                >
                    {(props) => (
                        <Form>
                            <VStack spacing={6} align="stretch">
                                <SectionCard icon={faUser} title={t("userDashboardAccountSection")}>
                                    <Field name="email">
                                        {({ field }) => (
                                            <FormControl>
                                                <FormLabel mb={1} fontSize="sm" color="gray.600">
                                                    {t("emailLabel")}
                                                </FormLabel>
                                                <Input {...field} isDisabled={!editMode} {...inputStyles} />
                                                {currentUser?.unconfirmedEmail && (
                                                    <Text mt={2} fontSize="sm" color="orange.600">
                                                        {t("pendingEmailChangeMessage", {
                                                            email: currentUser.unconfirmedEmail,
                                                        })}
                                                    </Text>
                                                )}
                                            </FormControl>
                                        )}
                                    </Field>

                                    {editMode && (
                                        <Field name="currentPassword">
                                            {({ field, meta }) => (
                                                <FormControl
                                                    isInvalid={meta.touched && meta.error}
                                                    mt={4}
                                                >
                                                    <FormLabel mb={1} fontSize="sm" color="gray.600">
                                                        {t("currentPasswordLabel")}
                                                    </FormLabel>
                                                    <FormHelperText mb={2} fontSize="xs" color="gray.500">
                                                        {t("currentPasswordRequiredForSensitiveChangesMessage")}
                                                    </FormHelperText>
                                                    <PasswordInput
                                                        {...field}
                                                        isVisible={showCurrentPassword}
                                                        onToggle={() =>
                                                            setShowCurrentPassword((prev) => !prev)
                                                        }
                                                        placeholder={t("currentPasswordPlaceholder")}
                                                        {...inputStyles}
                                                    />
                                                    <FormErrorMessage>{meta.error}</FormErrorMessage>
                                                </FormControl>
                                            )}
                                        </Field>
                                    )}

                                    {editMode && (
                                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                                            <Field name="newPassword">
                                                {({ field, meta }) => (
                                                    <FormControl isInvalid={meta.touched && meta.error}>
                                                        <FormLabel mb={1} fontSize="sm" color="gray.600">
                                                            {t("newPasswordLabel")}
                                                        </FormLabel>
                                                        <PasswordInput
                                                            {...field}
                                                            isVisible={showNewPassword}
                                                            onToggle={() =>
                                                                setShowNewPassword((prev) => !prev)
                                                            }
                                                            placeholder={t("newPasswordPlaceholder")}
                                                            {...inputStyles}
                                                        />
                                                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                                                    </FormControl>
                                                )}
                                            </Field>
                                            <Field name="confirmPassword">
                                                {({ field, meta }) => (
                                                    <FormControl isInvalid={meta.touched && meta.error}>
                                                        <FormLabel mb={1} fontSize="sm" color="gray.600">
                                                            {t("confirmPasswordLabel")}
                                                        </FormLabel>
                                                        <PasswordInput
                                                            {...field}
                                                            isVisible={showConfirmPassword}
                                                            onToggle={() =>
                                                                setShowConfirmPassword((prev) => !prev)
                                                            }
                                                            placeholder={t("confirmPasswordPlaceholder")}
                                                            {...inputStyles}
                                                        />
                                                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                                                    </FormControl>
                                                )}
                                            </Field>
                                        </SimpleGrid>
                                    )}
                                </SectionCard>

                                {logoSection}

                                <SectionCard icon={faIdCard} title={t("userDashboardPersonalDetailsSection")}>
                                    {isPartner && (
                                        <Badge
                                            colorScheme="teal"
                                            alignSelf="flex-start"
                                        >
                                            {accountKindLabel}
                                        </Badge>
                                    )}

                                    {isOrgPartner ? (
                                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                            <Field name="organizationName">
                                                {({ field }) => (
                                                    <FormControl>
                                                        <FormLabel mb={1} fontSize="sm" color="gray.600">
                                                            {t("companyNameLabel")}
                                                        </FormLabel>
                                                        <Input {...field} isDisabled={!editMode} {...inputStyles} />
                                                    </FormControl>
                                                )}
                                            </Field>
                                            <Field name="contactPersonName">
                                                {({ field }) => (
                                                    <FormControl>
                                                        <FormLabel mb={1} fontSize="sm" color="gray.600">
                                                            {t("contactPersonNameLabel")}
                                                        </FormLabel>
                                                        <Input {...field} isDisabled={!editMode} {...inputStyles} />
                                                    </FormControl>
                                                )}
                                            </Field>
                                        </SimpleGrid>
                                    ) : (
                                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                            <Field name="firstName">
                                                {({ field }) => (
                                                    <FormControl>
                                                        <FormLabel mb={1} fontSize="sm" color="gray.600">
                                                            {t("firstNameLabel")}
                                                        </FormLabel>
                                                        <Input {...field} isDisabled={!editMode} {...inputStyles} />
                                                    </FormControl>
                                                )}
                                            </Field>
                                            <Field name="lastName">
                                                {({ field }) => (
                                                    <FormControl>
                                                        <FormLabel mb={1} fontSize="sm" color="gray.600">
                                                            {t("lastNameLabel")}
                                                        </FormLabel>
                                                        <Input {...field} isDisabled={!editMode} {...inputStyles} />
                                                    </FormControl>
                                                )}
                                            </Field>
                                        </SimpleGrid>
                                    )}

                                    <Field name="telephoneNumber">
                                        {({ field, form }) => (
                                            <FormControl
                                                isInvalid={
                                                    form.errors.telephoneNumber &&
                                                    form.touched.telephoneNumber
                                                }
                                            >
                                                <FormLabel mb={1} fontSize="sm" color="gray.600">
                                                    {t("telephoneLabel")}
                                                    <Text as="span" color="gray.500" fontWeight="normal">
                                                        {" "}
                                                        ({t("phoneNumberOptionalHint")})
                                                    </Text>
                                                </FormLabel>
                                                <Input {...field} isDisabled={!editMode} {...inputStyles} />
                                                <FormErrorMessage>
                                                    {form.errors.telephoneNumber}
                                                </FormErrorMessage>
                                            </FormControl>
                                        )}
                                    </Field>
                                </SectionCard>

                                {isGuest && (
                                    <SectionCard icon={faLock} title={t("userDashboardTravelDocumentsSection")}>
                                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                            <Field name="passportExpiresAt">
                                                {({ field }) => (
                                                    <FormControl>
                                                        <FormLabel mb={1} fontSize="sm" color="gray.600">
                                                            {t("passportExpiryLabel")}
                                                        </FormLabel>
                                                        <Input
                                                            type="date"
                                                            {...field}
                                                            isDisabled={!editMode}
                                                            {...inputStyles}
                                                        />
                                                    </FormControl>
                                                )}
                                            </Field>
                                        </SimpleGrid>
                                        <Field name="nameOnPassport">
                                            {({ field }) => (
                                                <FormControl>
                                                    <FormLabel mb={1} fontSize="sm" color="gray.600">
                                                        {t("nameOnPassportLabel")}
                                                    </FormLabel>
                                                    <Input {...field} isDisabled={!editMode} {...inputStyles} />
                                                </FormControl>
                                            )}
                                        </Field>
                                    </SectionCard>
                                )}

                                {editMode && (
                                    <Button
                                        type="submit"
                                        colorScheme="teal"
                                        size="md"
                                        borderRadius="md"
                                        alignSelf="flex-start"
                                        px={8}
                                        leftIcon={<FontAwesomeIcon icon={faSave} />}
                                        isLoading={props.isSubmitting}
                                        boxShadow="sm"
                                    >
                                        {t("saveChangesButton")}
                                    </Button>
                                )}
                            </VStack>
                        </Form>
                    )}
                </Formik>
            </Box>
        </Box>
    );
};

export default UserDashboard;
